import { createRouter, createWebHistory, type LocationQueryValue, type RouteLocationGeneric } from 'vue-router'

import { isCustomerAccount, resolveCustomerCaseRedirect } from '@/composables/customerAccess'
import { CONNECTION_PATH, LANDING_PATH, LANDING_PUBLIC } from '@/pages/pageLanding/content/visibility'
import { useAuthStore } from '@/stores/authStore'
import { useCaseStore } from '@/stores/caseStore/0_caseStore'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { COMPANY_ROLE_VALUES } from '@/utils/organizationRoles'
import { decryptID, encryptID, normalizeCaseId } from '@/utils/security'
import { useDetailviewStore } from './stores/detailviewStore'

const routes = [
  // public pages
  // Landingpage unter LANDING_PATH; / führt nur mit LANDING_PUBLIC dorthin,
  // sonst zum Login (siehe content/visibility.ts).
  {
    path: LANDING_PATH,
    name: 'PageLanding',
    component: () => import('@/pages/pageLanding/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
      marketing: true,
    },
  },
  {
    path: CONNECTION_PATH,
    name: 'PageConnection',
    component: () => import('@/pages/pageConnection/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
      marketing: true,
    },
  },
  {
    path: '/',
    redirect: LANDING_PUBLIC ? LANDING_PATH : { name: 'PageLogin' },
    meta: {
      isMainView: false,
      requiresAuth: false,
    },
  },
  {
    path: '/preise',
    name: 'PagePricing',
    component: () => import('@/pages/pagePricing/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
      marketing: true,
    },
  },
  {
    path: '/apps/:slug',
    name: 'PageAppDetail',
    component: () => import('@/pages/pageAppDetail/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
      marketing: true,
    },
  },
  {
    // Kundenfeedback 03.11.2026 (I): Abmeldelink aus Einladungsmails –
    // öffentlich, signiertes Token im Pfad.
    path: '/einladungen/abmelden/:token',
    name: 'PageInvitationOptOut',
    component: () => import('@/pages/pageInvitationOptOut/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
    },
  },
  {
    path: '/einloggen',
    name: 'PageLogin',
    component: () => import('@/pages/pageLogin/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
    },
  },
  {
    path: '/datenschutz',
    name: 'PagePrivacyPolicy',
    component: () => import('@/pages/pagePrivacyPolicy/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
    },
  },
  {
    path: '/impressum',
    name: 'PageImprint',
    component: () => import('@/pages/pageImprint/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
    },
  },
  {
    path: '/signup',
    name: 'PageSignup',
    component: () => import('@/pages/pageSignup/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
    },
  },
  {
    path: '/signup-customer',
    name: 'PageSignupCustomer',
    component: () => import('@/pages/pageSignupCustomer/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
    },
  },
  {
    path: '/onboarding',
    name: 'PageOnboarding',
    component: () => import('@/pages/pageOnboarding/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: true,
      allowedUserRoles: ['Administrator'],
      skipOnboardingGuard: true,
    },
  },
  {
    path: '/onboarding/verknuepfung',
    name: 'PageOnboardingLinkOrg',
    component: () => import('@/pages/pageOnboardingLinkOrg/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: true,
      allowedUserRoles: ['Administrator'],
      skipOnboardingGuard: true,
    },
  },
  {
    path: '/verknuepfung/:token',
    name: 'PageOrgLinkRespond',
    component: () => import('@/pages/pageOrgLinkRespond/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: true,
      skipOnboardingGuard: true,
    },
  },

  // Apple-like platform hub: choose company + app after login.
  {
    path: '/willkommen',
    name: 'PageWelcomeHub',
    component: () => import('@/pages/pageWelcomeHub/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: true,
    },
  },

  // Startroute für KARS-HR-Links (z. B. aus Mails): /kars-hr/oeffnen?next=/umfragen/5.
  // Nach der Anmeldung geht es per SSO direkt auf die Seite in KARS-HR
  // statt auf die Willkommensseite (Kundenfeedback 15.10.2026).
  {
    path: '/kars-hr/oeffnen',
    name: 'PageHrLaunch',
    component: () => import('@/pages/pageHrLaunch/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: true,
    },
  },

  // general navigation page
  {
    path: '/dashboard',
    name: 'PageDashboard',
    component: () => import('@/pages/pageDashboard/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
    },
  },
  {
    path: '/faelle',
    name: 'PageCases',
    component: () => import('@/pages/pageCases/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
    },
  },
  {
    path: '/wiedervorlagen',
    name: 'PageReminders',
    component: () => import('@/pages/pageReminders/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
      allowedOrganizationRoles: COMPANY_ROLE_VALUES,
    },
  },
  {
    path: '/adressen',
    name: 'PageAddresses',
    redirect: '/kontakte',
  },
  {
    path: '/kontakte',
    name: 'PageContacts',
    component: () => import('@/pages/pageContacts/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
      allowedOrganizationRoles: COMPANY_ROLE_VALUES,
    },
  },
  {
    // Issue #349: Fahrzeug-Stammdatenbank (eigene/Kunden-Fahrzeuge).
    path: '/fahrzeuge',
    name: 'PageVehiclesMaster',
    component: () => import('@/pages/pageVehiclesMaster/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
      allowedOrganizationRoles: COMPANY_ROLE_VALUES,
    },
  },
  {
    // Versicherungsbedingungen (Kundenfeedback 25.10.2026 Teil 2, VII; seit
    // 26.10.2026, IX zentrale KARS-Bibliothek für Kasko und Kfz-Haftpflicht):
    // alle Nutzer lesen, Unternehmen reichen ein, KARS gibt frei.
    // Query: versicherer, stichtag (JJJJ-MM-TT), art (zulassung|wechsel),
    // sparte (kasko|haftpflicht), quelle=akte, grundlage (versichert|erstzulassung).
    // Lesen dürfen alle angemeldeten Nutzer (auch die Plattform-
    // Administration, deren Organisation keine Unternehmensrolle hat);
    // Light-Nutzer hält das allgemeine Light-User-Gate fern.
    path: '/versicherungsbedingungen',
    name: 'PageKaskoConditions',
    component: () => import('@/pages/pageKaskoConditions/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
    },
  },
  {
    // Bisherige Adresse (Lesezeichen, Links aus Akten): Query bleibt erhalten.
    path: '/kaskobedingungen',
    redirect: (to: RouteLocationGeneric) => ({ path: '/versicherungsbedingungen', query: to.query }),
  },
  {
    // Globale Kommunikations-Seite: Stage + 3 Kacheln (Nachrichten,
    // Telefonate, Postausgang) analog zur Fall-Ebene.
    path: '/kommunikation',
    name: 'PageCommunication',
    component: () => import('@/pages/pageCommunication/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
    },
  },
  {
    // Kommunikationseinstellungen: Archiv-Postfach + E-Mail-Signatur.
    // Zuvor als Tab innerhalb der Organisationseinstellungen, jetzt eigene
    // Seite (erreichbar über das Header-Menü "Kommunikation").
    path: '/kommunikation/einstellungen',
    name: 'PageCommunicationSettings',
    component: () => import('@/pages/pageCommunicationSettings/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
      allowedOrganizationRoles: COMPANY_ROLE_VALUES,
      allowedUserRoles: ["Administrator", "Unternehmensleiter", "Standortleiter"],
    },
  },
  {
    // Zentraler Posteingang (pages/pageInbox) ist vorerst ausgeblendet
    // (Kundenfeedback 13.09.26). Die URL bleibt als Redirect auf
    // Korrespondenz › E-Mails erhalten; ?inbox=<id> öffnet den Eintrag dort.
    path: '/posteingang',
    name: 'PageInbox',
    redirect: (to: RouteLocationGeneric) => ({ name: 'PageCommunication', query: { tab: 'nachrichten', ...to.query } }),
  },
  {
    // Alt-URL bleibt als Redirect erhalten.
    path: '/nachrichten',
    name: 'PageMessages',
    redirect: (to: RouteLocationGeneric) => ({ name: 'PageCommunication', query: { tab: 'nachrichten', ...to.query } }),
  },
  {
    // Downloadbereich: zentrale Dokumente der Organisation (Vorlagen,
    // Formulare etc.) für alle angemeldeten Org-Mitglieder.
    path: "/downloads",
    name: "PageDownloads",
    component: () => import("@/pages/pageDownloads/Page.vue"),
    meta: {
      isMainView: true,
      requiresAuth: true,
    },
  },
  {
    path: "/organisation",
    name: "PageOrganization",
    component: () => import("@/pages/pageOrganization/Page.vue"),
    meta: {
      isMainView: true,
      requiresAuth: true,
      // Plattform-Seite von KARS-Connection: immer mit der KARS-Connection-
      // Leiste (Logo + Zurück), nie mit der KARS-Schaden-Navigation — egal,
      // über welchen Weg sie geöffnet wurde (Kundenfeedback 05.09.26, IV).
      connectionPage: true,
      allowedOrganizationRoles: COMPANY_ROLE_VALUES,
      allowedUserRoles: ["Administrator", "Unternehmensleiter", "Standortleiter"],
    },
  },
  {
    path: '/stammdaten',
    name: 'PageCoreData',
    component: () => import('@/pages/pageCoreData/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
      allowedOrganizationRoles: ['Privatkunde', 'Zeuge'],
    },
  },
  {
    path: '/features',
    name: 'PageFeatures',
    component: () => import('@/pages/pageFeatures/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
    },
  },
  {
    path: '/hilfe',
    name: 'PageHelp',
    component: () => import('@/pages/pageHelp/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
    },
  },

  /* personal routes */
  {
    path: '/benutzereinstellungen',
    name: 'PageUserSettings',
    component: () => import('@/pages/pageUserSettings/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
    },
  },
  {
    path: '/kalender',
    name: 'PageCalendar',
    component: () => import('@/pages/pageCalendar/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
      // Kundenfeedback Kanzlei Polz 27.10.2026 (III): persönlicher Kalender
      // für alle Unternehmen (bisher nur Sachverständige).
      allowedOrganizationRoles: COMPANY_ROLE_VALUES,
    },
  },
  {
    // Die eigene Einstellungsseite entfällt (20.10.2026): dieselben
    // Einstellungen liegen global in KARS-Connection. Alte Links und
    // Lesezeichen landen dort.
    path: '/einstellungen',
    redirect: { path: '/organisation', query: { from: 'hub' } },
  },
  {
    path: '/einstellungen/kalender',
    name: 'PageCalendarSettings',
    component: () => import('@/pages/pageCalendarSettings/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
    },
  },
  {
    // Die frühere Abonnement-Seite (/abonnement) wurde entfernt —
    // KI-Kontingente und Buchungen liegen auf /rechnungen (30.08.26).
    path: '/abonnement',
    redirect: (to: RouteLocationGeneric) => ({ name: 'PageInvoices', query: to.query }),
  },
  {
    path: "/berechtigungen",
    name: "PagePermissions",
    component: () => import("@/pages/pagePermissions/Page.vue"),
    meta: {
      isMainView: true,
      requiresAuth: true,
      allowedUserRoles: ["Administrator", "Unternehmensleiter", "Standortleiter"],
    },
  },
  {
    path: '/rechnungen',
    name: 'PageInvoices',
    component: () => import('@/pages/pageInvoices/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
      connectionPage: true,
      allowedUserRoles: ['Administrator'],
    },
  },

  /* case detail page */
  {
    path: '/faelle/:idCase',
    component: () => import('@/pages/pageCases/pageCaseDetail/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
    },
    // Die Detailseite und ihre Sections erwarten die KLARTEXT-ID. Verschlüsselte
    // IDs (alte Bookmarks, vergiftete "Zuletzt geöffnet"-Einträge) werden hier
    // zentral auf die Klartext-URL umgeschrieben — sonst laufen alle Requests
    // auf einen nicht existierenden Fall → 403 "keine Berechtigung".
    beforeEnter: (to: RouteLocationGeneric) => {
      const plain = normalizeCaseId(to.params.idCase)
      if (plain !== null && String(plain) !== String(to.params.idCase)) {
        return {
          name: to.name ?? 'PageCaseDetail',
          params: { ...to.params, idCase: String(plain) },
          query: to.query,
          hash: to.hash,
          replace: true,
        }
      }
      return true
    },
    redirect: (to: any) => ({ name: 'PageCaseDetail', params: to.params }),
    children: [
      {
        path: 'cockpit',
        name: 'PageCaseDetail',
        component: () => import('@/pages/pageCases/pageCaseDetail/sections/SectionCockpitHost.vue'),
        meta: { isMainView: true, requiresAuth: true },
      },
      {
        path: 'daten',
        name: 'PageCaseDetailDaten',
        component: () => import('@/pages/pageCases/pageCaseDetail/sections/SectionDaten.vue'),
        meta: { isMainView: true, requiresAuth: true },
      },
      {
        path: 'dokumente',
        name: 'PageCaseDetailDokumente',
        component: () => import('@/pages/pageCases/pageCaseDetail/sections/SectionDokumente.vue'),
        meta: { isMainView: true, requiresAuth: true },
      },
      {
        path: 'beteiligte',
        name: 'PageCaseDetailBeteiligte',
        component: () => import('@/pages/pageCases/pageCaseDetail/sections/SectionBeteiligte.vue'),
        meta: { isMainView: true, requiresAuth: true },
      },
      {
        path: 'nachrichten',
        name: 'PageCaseDetailNachrichten',
        meta: { isMainView: true, requiresAuth: true },
        // Ursprüngliche Query (z. B. ?conversation=N aus der Glocke) erhalten,
        // damit SectionNachrichten die konkrete Unterhaltung direkt öffnet.
        redirect: (to: any) => ({ name: 'PageCaseDetailKommunikation', params: to.params, query: { ...to.query, tab: 'nachrichten' } }),
      },
      {
        path: 'kommunikation',
        name: 'PageCaseDetailKommunikation',
        component: () => import('@/pages/pageCases/pageCaseDetail/sections/SectionKommunikation.vue'),
        meta: { isMainView: true, requiresAuth: true },
      },
      {
        path: 'termine',
        name: 'PageCaseDetailTermine',
        component: () => import('@/pages/pageCases/pageCaseDetail/sections/SectionTermine.vue'),
        meta: { isMainView: true, requiresAuth: true },
      },
      {
        path: 'telefonate',
        name: 'PageCaseDetailTelefonate',
        meta: { isMainView: true, requiresAuth: true },
        redirect: (to: any) => ({ name: 'PageCaseDetailKommunikation', params: to.params, query: { tab: 'telefonate' } }),
      },
      {
        path: 'historie',
        name: 'PageCaseDetailHistorie',
        component: () => import('@/pages/pageCases/pageCaseDetail/sections/SectionHistorie.vue'),
        meta: { isMainView: true, requiresAuth: true },
      },
      // Regressbereich (Inhaber-Feedback 15.08.26 II.3): Gegenüberstellungen,
      // Prüfbericht-Auswertungen, Regressakte anlegen.
      {
        path: 'regress',
        name: 'PageCaseDetailRegress',
        component: () => import('@/pages/pageCases/pageCaseDetail/sections/SectionRegress.vue'),
        meta: { isMainView: true, requiresAuth: true },
      },
    ],
  },

  /* super admin area */
  {
    path: '/admin',
    component: () => import('@/pages/pageAdmin/AdminLayout.vue'),
    meta: {
      isMainView: false,
      requiresAuth: true,
      allowedUserRoles: ['SuperAdmin'],
    },
    redirect: { name: 'AdminDashboard' },
    children: [
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: () => import('@/pages/pageAdmin/PageDashboard.vue'),
        meta: { isMainView: false, requiresAuth: true, allowedUserRoles: ['SuperAdmin'] },
      },
      {
        path: 'organisationen',
        name: 'AdminOrganizations',
        component: () => import('@/pages/pageAdmin/PageOrganizations.vue'),
        meta: { isMainView: false, requiresAuth: true, allowedUserRoles: ['SuperAdmin'] },
      },
      {
        path: 'benutzer',
        name: 'AdminUsers',
        component: () => import('@/pages/pageAdmin/PageUsers.vue'),
        meta: { isMainView: false, requiresAuth: true, allowedUserRoles: ['SuperAdmin'] },
      },
      {
        path: 'faelle',
        name: 'AdminCases',
        component: () => import('@/pages/pageAdmin/PageCases.vue'),
        meta: { isMainView: false, requiresAuth: true, allowedUserRoles: ['SuperAdmin'] },
      },
      {
        path: 'abonnements',
        name: 'AdminSubscriptions',
        component: () => import('@/pages/pageAdmin/PageSubscriptions.vue'),
        meta: { isMainView: false, requiresAuth: true, allowedUserRoles: ['SuperAdmin'] },
      },
      {
        // Kundenfeedback 26.10.2026, IX: Einreichungen zu den Versicherungsbedingungen prüfen.
        path: 'versicherungsbedingungen',
        name: 'AdminInsuranceConditions',
        component: () => import('@/pages/pageAdmin/PageInsuranceConditions.vue'),
        meta: { isMainView: false, requiresAuth: true, allowedUserRoles: ['SuperAdmin'] },
      },
      {
        // Demo-Modus: KARS unter vorgewählter Rolle vorführen, ohne E-Mail-Versand.
        path: 'demo',
        name: 'AdminDemo',
        component: () => import('@/pages/pageAdmin/PageDemo.vue'),
        meta: { isMainView: false, requiresAuth: true, allowedUserRoles: ['SuperAdmin'] },
      },
      {
        path: 'roadmap',
        name: 'AdminRoadmap',
        component: () => import('@/pages/pageAdmin/PageRoadmap.vue'),
        meta: { isMainView: false, requiresAuth: true, allowedUserRoles: ['SuperAdmin'] },
      },
    ],
  },

  /* questionaire and summary pages */
  {
    path: '/fragebogen/:idCase',
    name: 'PageQuestionnaire',
    component: () => import('@/pages/pageQuestionnaire/0_pageQuestionnaire/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
    },
  },
  {
    path: '/zusammenfassung/:idCase',
    name: 'PageSummary',
    component: () => import('@/pages/pageSummary/Page.vue'),
    meta: {
      isMainView: true,
      requiresAuth: true,
    },
  },

  // Public Vollmacht signing page (no auth, token-based)
  {
    path: '/vollmacht-unterschreiben',
    name: 'PageVollmachtSign',
    component: () => import('@/pages/pageVollmachtSign/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
    },
  },

  // Public Zeugenfragebogen page (no auth, token-based) — Issue #409
  {
    path: '/zeugenfragebogen',
    name: 'PageWitnessForm',
    component: () => import('@/pages/pageWitnessForm/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
    },
  },

  // Public mobile upload page (no auth, QR code token-based)
  {
    path: '/m/upload/:token',
    name: 'PageMobileUpload',
    component: () => import('@/pages/pageMobileUpload/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
    },
  },

  // Public Vor-Ort-Fragebogen (no auth, QR/Link token-based)
  {
    path: '/vorort',
    name: 'PageOnsiteQuestionnaire',
    component: () => import('@/pages/pageOnsiteQuestionnaire/Page.vue'),
    meta: {
      isMainView: false,
      requiresAuth: false,
    },
  },

  { path: '/:pathMatch(.*)*', redirect: '/einloggen' },
]

/** Erster Wert eines Query-Parameters (bei ?seite=1&seite=2 der erste). */
function firstQueryValue (value: LocationQueryValue | LocationQueryValue[] | undefined): string | null {
  const raw = Array.isArray(value) ? value[0] : value
  return raw ?? null
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior (to, from, savedPosition) {
    // Marketing-Seiten scrollen selbst (Lenis): Anker gleiten weich, und ein
    // reiner Query-Wechsel (z. B. Zielgruppen-Tab ?fuer=) springt nicht nach oben.
    if (to.meta.marketing && (to.hash || to.path === from.path)) {
      return false
    }
    // Blättern in einer Liste (?seite=, Fallübersicht): Die Seite scrollt
    // selbst zur Trefferzahl – ein Sprung nach ganz oben wäre falsch.
    if (to.name === from.name && firstQueryValue(to.query.seite) !== firstQueryValue(from.query.seite)) {
      return false
    }
    if (to.hash) {
      return { el: to.hash, top: 80, behavior: 'smooth' }
    }
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

function normalizeCaseIdFromRoute (rawId: unknown): number | null {
  if (rawId === null || rawId === undefined) {
    return null
  }
  const asString = String(rawId).trim()
  if (!asString) {
    return null
  }

  // Verschlüsselte IDs sind ebenfalls reine Zahlen, daher zuerst Decrypt
  // versuchen. Schlägt das fehl (z. B. weil die Zahl nicht zur Encrypt-
  // Formel passt), fallback auf Plain-Integer.
  const decrypted = decryptID(asString)
  if (decrypted !== null) {
    return decrypted
  }

  const parsed = Number(asString)
  if (Number.isInteger(parsed) && parsed > 0) {
    return parsed
  }

  return null
}

router.beforeEach(async (to, from, next) => {
  const detailview = useDetailviewStore()

  const guardResult = await detailview.performGuardedAction(() => {})
  if (guardResult === false) {
    // Benutzer hat im "Ungespeicherte Änderungen"-Dialog "Fortsetzen"
    // gewählt: Navigation abbrechen, Eingabe bleibt erhalten.
    next(false)
    return
  }

  if (to.meta.requiresAuth) {
    const authStore = useAuthStore()

    try {
      if (!authStore.user) {
        await authStore.fetchUserForRouterGuard()
      }

      const isAuthenticated = authStore.isAuthenticated
      if (!isAuthenticated) {
        // Preserve intended destination and query params for redirect after login
        next({
          name: 'PageLogin',
          query: {
            redirect: to.path,
            ...to.query, // Preserves caseId and any other params
          },
        })
        return
      }

      const organizationRoles = authStore.user?.organizationRoles ?? []
      const userRole = authStore.user?.userRole ?? null

      const allowedOrganizationRoles = to.meta.allowedOrganizationRoles as string[] | undefined
      const allowedUserRoles = to.meta.allowedUserRoles as string[] | undefined

      // Bei fehlender Berechtigung landet man auf der Willkommensseite,
      // wenn die Seite aus dem Hub geöffnet wurde — nicht unvermittelt im
      // KARS-Schaden-Dashboard (Kundenfeedback 19.08.26, XII).
      const deniedTarget = to.query.from === 'hub'
        ? { name: 'PageWelcomeHub' }
        : { name: 'PageDashboard' }

      if (allowedOrganizationRoles && !allowedOrganizationRoles.some(r => organizationRoles.includes(r))) {
        next(deniedTarget)
        return
      }

      if (allowedUserRoles && userRole && !allowedUserRoles.includes(userRole)) {
        next(deniedTarget)
        return
      }

      // ── Light-User-Gate ─────────────────────────────────────────────
      // Light users (Beteiligte mit eingeschränktem Portal-Zugang) dürfen
      // ausschließlich ihren gebundenen Fall sehen sowie einige Self-
      // Service-Seiten (Benutzereinstellungen, Hilfe).
      if (authStore.user?.accessTier === 'light') {
        const idLightUserCase = authStore.user.idLightUserCase

        const lightAllowedRouteNames = new Set<string>([
          'PageUserSettings',
          'PageHelp',
          'PageLogin',
          'PagePrivacyPolicy',
          'PageImprint',
        ])

        const routeName = typeof to.name === 'string' ? to.name : ''
        const isLightAllowed = lightAllowedRouteNames.has(routeName)

        const isCaseDetailRoute = routeName.startsWith('PageCaseDetail')
        const isCaseBoundQuestionnaireRoute = routeName === 'PageQuestionnaire' || routeName === 'PageSummary'
        const routeCaseId = normalizeCaseIdFromRoute(to.params?.idCase)
        const isOwnCase
          = (isCaseDetailRoute || isCaseBoundQuestionnaireRoute)
            && idLightUserCase !== null
            && routeCaseId === idLightUserCase

        // Pflicht-Fragebogen: Solange der Fall im Status "Fallaufnahme"
        // ist, hat der Light-User den Unfallfragebogen noch nicht
        // eingereicht. In diesem Fall wird JEDE Navigation (außer der
        // Self-Service-Allowlist und der Fragebogen-Seite selbst) auf
        // PageQuestionnaire umgeleitet. Erst nach erfolgreichem Submit
        // ändert sich der Status und der Gate gibt den Nutzer frei.
        const mustFillQuestionnaire
          = authStore.user.lightUserCaseStatus === 'Fallaufnahme'
            && idLightUserCase !== null
        const isOnOwnQuestionnaire
          = routeName === 'PageQuestionnaire'
            && idLightUserCase !== null
            && routeCaseId === idLightUserCase

        // Der Light-User darf während der Fallaufnahme zusätzlich sein eigenes
        // Cockpit (Fall-Detailansicht) sehen. So landet ein frisch
        // registrierter Mandant erst im Cockpit mit Willkommens-Overlay und
        // wird nicht ungefragt in den Fragebogen "überfahren". Andere Routen
        // werden weiterhin auf den Pflicht-Fragebogen umgeleitet.
        if (mustFillQuestionnaire && !isLightAllowed && !isOnOwnQuestionnaire && !isOwnCase) {
          next({
            name: 'PageQuestionnaire',
            params: { idCase: String(encryptID(idLightUserCase!)) },
          })
          return
        }

        if (!isLightAllowed && !isOwnCase) {
          if (idLightUserCase) {
            next({
              name: 'PageCaseDetailKommunikation',
              params: { idCase: String(idLightUserCase) },
            })
          } else {
            next({ name: 'PageLogin' })
          }
          return
        }
      }

      // ── Mandanten-Gate (Kundensicht) ────────────────────────────────
      // Mandanten (Privat-/Firmenkunde, Zeuge, Light-User) sehen von der
      // Fallakte nur Cockpit, Korrespondenz, Kommunikation und Termine –
      // Daten, Beteiligte und Historie sind auch per URL nicht erreichbar
      // (Kanzlei-Anforderung: Änderungen nur im Fragebogen). Die Liste
      // lebt in composables/customerAccess.ts (CUSTOMER_ALLOWED_CASE_ROUTES).
      if (isCustomerAccount(authStore.user)) {
        const customerRedirect = resolveCustomerCaseRedirect(to.name)
        if (customerRedirect) {
          next({ name: customerRedirect, params: to.params })
          return
        }
      }

      // ── Onboarding-Pflicht ──────────────────────────────────────────
      // Nur Administratoren werden in den Wizard geführt. Mitarbeiter,
      // Standortleiter und private Nutzer sehen ihn nicht.
      const skipOnboarding = to.meta.skipOnboardingGuard === true
      const isMandantAccount
        = organizationRoles.includes('Privatkunde')
          || organizationRoles.includes('Zeuge')
          || organizationRoles.includes('Firmenkunde')
      if (!skipOnboarding && userRole === 'Administrator' && !isMandantAccount) {
        try {
          const onboardingStore = useOnboardingStore()
          await onboardingStore.ensureLoaded(authStore.user!.idOrganization)
          if (onboardingStore.needsOnboarding) {
            next({ name: 'PageOnboarding' })
            return
          }
        } catch (error) {
          // If status check fails we let the user through rather than locking
          // them out of the app.
          console.warn('Onboarding-Status konnte nicht geladen werden:', error)
        }
      }
    } catch (error: any) {
      // Only log unexpected errors (not authentication failures which are expected)
      const errorCode = error?.errorCode
      if (errorCode !== 401 && errorCode !== 403) {
        console.error('Error fetching user in router guard:', error)
      }

      // Preserve intended destination and query params for redirect after login
      next({
        name: 'PageLogin',
        query: {
          redirect: to.path,
          ...to.query, // Preserves caseId and any other params
        },
      })
      return
    }
  }

  next()
})

router.afterEach((to, from) => {
  // Tab-Titel je nach geöffnetem Modul (Kundenfeedback 05.09.26): Seiten der
  // Fachanwendung (isMainView) zeigen "KARS-Schaden", Plattform-Seiten (Hub,
  // Login, Admin, öffentliche Seiten) "KARS-Connection". Im Hub-Fokus-Modus
  // (from=hub, ohne Schaden-Navigation) bleibt es bei "KARS-Connection".
  // Marketing-Seiten setzen ihren Titel anschließend selbst (useMarketingPage);
  // KARS-HR läuft als eigene App mit eigenem Titel.
  const isSchadenApp = to.meta.isMainView === true && to.query.from !== 'hub' && to.meta.connectionPage !== true
  document.title = isSchadenApp ? 'KARS-Schaden' : 'KARS-Connection'

  const pagesToResetCaseStore = ['PageQuestionnaire', 'PageSummary', 'PageCaseDetailDaten']
  if (from.name && pagesToResetCaseStore.includes(from.name.toString())) {
    // Don't reset when only the query string changes (e.g. $router.replace({ query })
    // inside handleRouteDialog). Only reset when leaving the case context entirely
    // or navigating to a different case.
    const sameRoute = to.name === from.name
    const sameCase = to.params.idCase === from.params.idCase
    if (!sameRoute || !sameCase) {
      useCaseStore().resetCase()
    }
  }
})

export default router
